import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userAPI, UserUsage, UsageHistory, Credits } from '../services/user';
import { useTranslation } from 'react-i18next';

export default function UsagePage() {
  const [usage, setUsage] = useState<UserUsage | null>(null);
  const [history, setHistory] = useState<UsageHistory[]>([]);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const monthNames = t('usage.months', { returnObjects: true }) as string[];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usageData, historyData, creditsData] = await Promise.all([
          userAPI.getUsage(),
          userAPI.getUsageHistory(6),
          userAPI.getCredits(),
        ]);

        setUsage(usageData);
        setHistory(historyData.history);
        setCredits(creditsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('usage.error'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [t]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <svg className="w-8 h-8 animate-spin mx-auto text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="mt-2 text-slate-500 dark:text-slate-400">{t('usage.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!usage) return null;

  const usagePercent = Math.min((usage.analysesCount / usage.analysesLimit) * 100, 100);
  const isLimitReached = usage.analysesCount >= usage.analysesLimit;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('usage.title')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {t('usage.subtitle')}
        </p>
      </div>

      {/* Current Usage */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {t('usage.currentPeriod')}
          </h2>
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${
            usage.tier === 'max'
              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
              : usage.tier === 'pro'
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
          }`}>
            {usage.tier === 'max' ? 'MAX' : usage.tier === 'pro' ? 'PRO' : t('usage.freeTier')}
          </span>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-600 dark:text-slate-400">{t('usage.usedAnalyses')}</span>
              <span className="font-medium text-slate-900 dark:text-white">
                {usage.analysesCount} / {usage.analysesLimit}
              </span>
            </div>
            <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  isLimitReached ? 'bg-red-500' : 'bg-blue-600'
                }`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
            {isLimitReached && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                {t('usage.limitReached')}{usage.tier !== 'max' && (usage.tier === 'free' ? ` ${t('usage.upgradeToProOrMax')}` : ` ${t('usage.upgradeToMax')}`)}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('usage.tokensUsed')}</p>
              <p className="text-xl font-semibold text-slate-900 dark:text-white">
                {(usage.tokensUsed ?? 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('usage.availableCredits')}</p>
              <p className="text-xl font-semibold text-slate-900 dark:text-white">
                {usage.creditsBalance}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tier Limits */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          {t('usage.planLimits')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('usage.analysesPerMonth')}</p>
            <p className="text-lg font-semibold text-slate-900 dark:text-white">
              {usage.tierLimits.monthlyAnalyses}
            </p>
          </div>
          <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('usage.maxDocument')}</p>
            <p className="text-lg font-semibold text-slate-900 dark:text-white">
              {(usage.tierLimits.maxDocumentSize / 1000).toFixed(0)}k {t('usage.chars')}
            </p>
          </div>
          <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('usage.maxJudgments')}</p>
            <p className="text-lg font-semibold text-slate-900 dark:text-white">
              {usage.tierLimits.maxJudgments}
            </p>
          </div>
          <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('usage.pdfExport')}</p>
            <p className="text-lg font-semibold text-slate-900 dark:text-white">
              {usage.tierLimits.pdfExport ? t('usage.yes') : t('usage.no')}
            </p>
          </div>
        </div>

        {usage.tier !== 'max' && (
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              {usage.tier === 'free' ? t('usage.upgradeToProOrMaxLink') : t('usage.upgradeToMaxLink')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        )}
      </div>

      {/* Credits */}
      {credits && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('usage.credits')}</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('usage.creditsAvailable')}</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {credits.balance}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('usage.creditsPurchased')}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white">
                {credits.lifetimePurchased}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('usage.creditsPrice')}</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white">
                {credits.pricePerTen} PLN
              </p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              {t('usage.buyCredits')}
            </Link>
          </div>
        </div>
      )}

      {/* Usage History */}
      {history.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            {t('usage.history')}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <th className="pb-2 font-medium">{t('usage.period')}</th>
                  <th className="pb-2 font-medium text-right">{t('usage.analyses')}</th>
                  <th className="pb-2 font-medium text-right">{t('usage.tokens')}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr
                    key={`${item.year}-${item.month}`}
                    className="border-b border-slate-100 dark:border-slate-700 last:border-0"
                  >
                    <td className="py-3 text-slate-900 dark:text-white">
                      {monthNames[item.month - 1]} {item.year}
                    </td>
                    <td className="py-3 text-right text-slate-600 dark:text-slate-400">
                      {item.analysesCount}
                    </td>
                    <td className="py-3 text-right text-slate-600 dark:text-slate-400">
                      {(item.tokensUsed ?? 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
